import React, { useState } from 'react'
import { Typography, Tooltip, CircularProgress, IconButton, Box } from '@mui/material'
import { alpha } from '@mui/material/styles'
import LockIcon from '@mui/icons-material/Lock'
import HomeIcon from '@mui/icons-material/Home'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import DoorbellIcon from '@mui/icons-material/Doorbell'
import PeopleIcon from '@mui/icons-material/People'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VerifiedIcon from '@mui/icons-material/Verified'
import LinkIcon from '@mui/icons-material/Link'
import MapIcon from '@mui/icons-material/Map'

import {
  RoomCardContainer,
  RoomHeader,
  RoomTypeIcon,
  RoomTitleRow,
  PlayerCountBadge,
  RoomContent,
  RoomTitle,
  RoomDescription,
  RoomFooter,
  MembersChip,
  ActionButton,
  ActionButtons,
  ActionIconButton,
  ActionButtonsGroup,
  CreatorInfo,
  StatusBadge,
} from './styles/RoomCard.styles'
import { copyRoomJoinLink } from '../utils/roomJoinLink'
import { useAppSelector } from '../hooks'

export interface Room {
  id: string
  name: string
  description: string
  members: number
  isPublic: boolean
  creatorName?: string
  isVerified?: boolean
  status?: 'online' | 'offline' | 'busy'
  maxCapacity?: number
  mapId?: string
  mapName?: string
  customRoomId?: string
  companyId?: string | null
  /** The room's map has a reception, so outside guests can be invited (SC-422). */
  hasReception?: boolean
  metadata?: { companyId?: string | null; isLobby?: boolean }
}

export interface RoomCardProps {
  room: Room
  onJoin: (roomId: string) => Promise<void> | void
  onEdit?: (roomId: string) => void
  onDelete?: (roomId: string) => void
  isOwned?: boolean
  isJoining?: boolean
  disabled?: boolean
}

export default function RoomCard({
  room,
  onJoin,
  onEdit,
  onDelete,
  isOwned = false,
  isJoining = false,
  disabled = false,
}: RoomCardProps) {
  const userCompanyId = useAppSelector((s) => s.user.userData?.companyId ?? null)
  const [loading, setLoading] = useState<boolean>(isJoining)
  const [error, setError] = useState<string | null>(null)

  const handleJoin = async () => {
    try {
      setError(null)
      setLoading(true)

      if (onJoin.constructor.name === 'AsyncFunction') {
        await onJoin(room.id)
      } else {
        onJoin(room.id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room')
      console.error('Error joining room:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(room.id)
    }
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(room.id)
    }
  }

  const isAtCapacity = room.maxCapacity !== undefined && room.members >= room.maxCapacity

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await copyRoomJoinLink(room, userCompanyId)
    } catch (err) {
      console.error('Failed to copy join link', err)
    }
  }

  return (
    <RoomCardContainer elevation={1}>
      {isOwned && (
        <ActionButtons>
          <Tooltip title="Edit Room">
            <ActionIconButton size="small" className="edit" onClick={handleEdit}>
              <EditIcon />
            </ActionIconButton>
          </Tooltip>
          <Tooltip title="Delete Room">
            <ActionIconButton size="small" className="delete" onClick={handleDelete}>
              <DeleteIcon />
            </ActionIconButton>
          </Tooltip>
        </ActionButtons>
      )}

      {room.status && (
        <StatusBadge status={room.status}>
          {room.status === 'online' ? 'Active' : room.status === 'busy' ? 'Full' : 'Inactive'}
        </StatusBadge>
      )}

      <RoomHeader>
        <RoomTitleRow>
          {room.metadata?.isLobby ? (
            <Tooltip title="Company lobby">
              <RoomTypeIcon>
                <HomeIcon color="primary" />
              </RoomTypeIcon>
            </Tooltip>
          ) : (
            <RoomTypeIcon>
              <MeetingRoomIcon color="primary" />
            </RoomTypeIcon>
          )}
          <RoomTitle variant="h6">
            {room.name}
            {!room.isPublic && (
              <Tooltip title="Password Required">
                <LockIcon
                  fontSize="small"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.9rem',
                    color: 'warning.main',
                  }}
                />
              </Tooltip>
            )}
            {room.isVerified && (
              <Tooltip title="Verified Room">
                <VerifiedIcon color="primary" fontSize="small" sx={{ ml: 0.5, fontSize: '0.9rem' }} />
              </Tooltip>
            )}
          </RoomTitle>
        </RoomTitleRow>
      </RoomHeader>

      <RoomContent>
        <RoomDescription variant="body2">
          {room.description || ''}
        </RoomDescription>

        {/* The room's map, and a doorbell when that map has a reception */}
        {(room.mapName || room.mapId || room.hasReception) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            {(room.mapName || room.mapId) && (
              <Typography
                variant="caption"
                sx={(t) => ({
                  display: 'flex',
                  alignItems: 'center',
                  color: 'primary.main',
                  fontWeight: 500,
                  backgroundColor: alpha(t.palette.primary.main, 0.1),
                  borderRadius: '6px',
                  padding: '6px 10px',
                  width: 'fit-content',
                  gap: 0.5,
                  border: `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                })}
              >
                <MapIcon sx={{ fontSize: '0.9rem', color: 'primary.main', opacity: 0.9 }} />
                {room.mapName || room.mapId}
              </Typography>
            )}
            {room.hasReception && (
              <Tooltip title="This map has a reception, so you can invite outside guests to meetings here">
                <DoorbellIcon
                  aria-label="Has a reception"
                  data-testid="room-reception-icon"
                  sx={{ display: 'block', fontSize: '1.25rem', color: 'primary.main' }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {error && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'error.main',
              mb: 1,
              fontSize: '0.75rem',
            }}
          >
            {error}
          </Typography>
        )}
      </RoomContent>

      <RoomFooter>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <PlayerCountBadge>
            <PeopleIcon />
            {room.maxCapacity
              ? `${room.members}/${room.maxCapacity}`
              : `${room.members} ${room.members === 1 ? 'player' : 'players'}`
            }
          </PlayerCountBadge>
          <ActionButtonsGroup>
            <Tooltip title="Copy join link">
              <span>
                <IconButton 
                  size="small" 
                  onClick={handleCopyLink} 
                  disabled={loading}
                  sx={(t) => ({
                    color: 'text.secondary',
                    border: `1px solid ${t.palette.divider}`,
                    borderRadius: '6px',
                    p: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: alpha(t.palette.primary.main, 0.1),
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }
                  })}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <ActionButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleJoin}
              disabled={loading || isAtCapacity || disabled}
              startIcon={!room.isPublic && !loading && !disabled ? <LockIcon fontSize="small" /> : undefined}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isAtCapacity ? (
                'Full'
              ) : disabled ? (
                'Connecting\u2026'
              ) : (
                'Join'
              )}
            </ActionButton>
          </ActionButtonsGroup>
        </Box>
      </RoomFooter>
    </RoomCardContainer>
  )
}
